import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { authGet } from '../../../../services/apiService';
import { AzureDetail, AzureSummary } from './AzureView';
import { YearDetail, YearSummary } from './YearView';
import CaseRegister from './CaseRegister';
import IncidentsView from './IncidentsView';
import PerformanceView from './PerformanceView';
import { displayName, fmtDay, fmtDayLong, fmtInt, periodLabels, periodLine, windowText } from './briefingFormat';
import './briefing.css';

/** Every report view in tab order; the labels name the years from the briefing's own windows. */
const allModes = (periods) => {
  const { first, second } = periodLabels(periods);
  return [
    { key: 'yr', label: `${first} and ${second}` },
    { key: 'az', label: `${second} · Azure pre / post` },
    { key: 'inc', label: 'Incidents' },
    { key: 'perf', label: 'Client performance' }
  ];
};

const TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'detail', label: 'Detailed' },
  { key: 'register', label: 'Case register' }
];

const TabBar = ({ tab, onChange }) => (
  <div className="tabbar" role="tablist">
    {TABS.map((item) => (
      <button
        type="button"
        role="tab"
        key={item.key}
        aria-selected={tab === item.key ? 'true' : 'false'}
        onClick={() => onChange(item.key)}
      >
        {item.label}
      </button>
    ))}
  </div>
);

/**
 * Report views that exist for this briefing: the year view always, the Azure view only when the briefing has an
 * Azure move date, Incidents always, and Client performance when that payload exists.
 */
const availableModes = (briefing) => allModes(briefing.periods).filter((item) => {
  if (item.key === 'az') return Boolean(briefing.azure && briefing.periods.azureCutover);
  if (item.key === 'perf') return Boolean(briefing.performance);
  return true;
});

const Panel = ({ name, mode, tab, briefing, study, onStudy }) => {
  if (mode === 'inc') {
    return (
      <IncidentsView
        key={name}
        incidents={briefing.incidents}
        incidentsLoading={briefing.incidentsLoading}
      />
    );
  }
  if (mode === 'perf') return <PerformanceView performance={briefing.performance} />;
  if (tab === 'register') {
    const html = briefing.html || {};
    return (
      <CaseRegister
        key={`${mode}-${study.stamp}`}
        mode={mode}
        cases={(mode === 'yr' ? html.yrCases : html.azCases) || []}
        initial={mode === 'az' ? study.filters : null}
        cutover={briefing.periods.azureCutover ? fmtDayLong(briefing.periods.azureCutover) : ''}
        periods={briefing.periods}
      />
    );
  }
  if (mode === 'yr') return tab === 'summary' ? <YearSummary briefing={briefing} /> : <YearDetail briefing={briefing} />;
  return tab === 'summary'
    ? <AzureSummary briefing={briefing} onStudy={onStudy} activeSymptom={(study.filters && study.filters.symptom) || ''} />
    : <AzureDetail briefing={briefing} />;
};

/** How the case views are read: shared by the year and Azure views. */
const CASE_READING = 'Rates are per 30 days, so a part-month compares fairly with a whole one. Green is better for the service, red is worse.';

/**
 * What the active view shows and how to read it, from the briefing's own dates and sources, so the text stays true
 * whenever the page is opened. One short paragraph per view, not a list of every tab.
 * @returns {{ shows: string, reading: string }}
 */
const guideFor = (briefing, mode) => {
  const { periods, customer } = briefing;
  const { first, second } = periodLabels(periods);
  const asOf = briefing.generatedAt ? fmtDay(briefing.generatedAt) : null;
  const caseSource = `Source: Salesforce case data${asOf ? `, as of ${asOf}` : ''}.`;

  if (mode === 'yr') {
    const moved = periods.azureMovedOn
      ? ` A move to Azure on ${fmtDayLong(periods.azureMovedOn, true)} is recorded outside the period shown, so there is no before/after view.`
      : '';
    return {
      shows: `Compares ${first} (${windowText(periods.y25)}) with ${second} (${windowText(periods.y26)}): how many cases were raised, how quickly they were closed, and what they were about.${moved}`,
      reading: `${caseSource} Repeat reports count once and test tickets are left out. ${CASE_READING}`
    };
  }
  if (mode === 'az') {
    const cutover = periods.azureCutover;
    const dayBefore = new Date(new Date(cutover).getTime() - 24 * 60 * 60 * 1000).toISOString();
    return {
      shows: `Splits ${second} at the ${fmtDayLong(cutover, true)} move to Azure: ${fmtDayLong(periods.y26.start)} to ${fmtDayLong(dayBefore, true)} before, ${fmtDayLong(cutover)} to ${fmtDayLong(periods.y26.end, true)} after. It compares case rates, close times and open work.`,
      reading: `The move date is from the Migrations roster, and the move day counts as after. ${caseSource} ${CASE_READING}`
    };
  }
  if (mode === 'inc') {
    const who = customer && customer.name ? displayName(customer.name) : 'this customer';
    return {
      shows: `P1 Critical ServiceNow incidents for ${who} opened in ${second} (${windowText(periods.y26)}), up to 15. The incident names their instance or connect address, or a multi-word customer name, or a case on their account links that incident number. One card per incident.`,
      reading: 'Opened, impact, description, cause notes, close notes, closed date, priority and state come from the ServiceNow incident.'
    };
  }
  return {
    shows: `Response times and errors${customer.instanceName ? ` for ${customer.instanceName}` : ''} from web-server (Apache) logs, by month: typical response time, the slowest 5% of requests, requests asked to retry (rate limited) and server errors.`,
    reading: `Health checks, static files, bots and upload-progress polling are left out. A month showing a day range is still in progress. Source: Datadog.`
  };
};

const HowTo = ({ briefing, mode }) => {
  const { shows, reading } = guideFor(briefing, mode);
  return (
    <p className="howto">
      <b>What this page shows.</b> {shows}
      {' '}<b>How to read it.</b> {reading}
    </p>
  );
};

/**
 * Leadership briefing, opened from the Top Customers card at /briefings/<account name>.
 * The numbers come from the briefing API with the portal's own login, so there is no separate sign-in.
 */
export default function CustomerBriefingPage() {
  const { name } = useParams();
  const [briefing, setBriefing] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('az');
  const [tab, setTab] = useState('summary');
  const [study, setStudy] = useState({ stamp: 0, filters: null });

  useEffect(() => {
    let cancelled = false;
    setBriefing(null);
    setError('');
    (async () => {
      try {
        const res = await authGet(`/api/briefings/${encodeURIComponent(name)}`, null, 'json', { useCache: false });
        if (cancelled) return;
        if (!res || res.success === false || !res.briefing) {
          throw new Error((res && res.message) || 'Briefing response missing');
        }
        setBriefing(res.briefing);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load the briefing');
      }
    })();
    return () => { cancelled = true; };
  }, [name]);

  useEffect(() => {
    if (briefing) document.title = `${displayName(briefing.customer.name)} — engineering briefing`;
  }, [briefing]);

  // Clicking a chart segment or legend entry lists those cases in the register
  const handleStudy = useCallback((symptom, period) => {
    setStudy((prev) => ({ stamp: prev.stamp + 1, filters: { symptom: symptom || '', period: period || '' } }));
    setMode('az');
    setTab('register');
  }, []);

  const changeMode = (next) => {
    setMode(next);
    setTab('summary');
  };

  const modes = briefing ? availableModes(briefing) : [];
  // The view asked for may not exist for this briefing (no Azure move date): fall back to the first one that does
  const activeMode = modes.some((item) => item.key === mode) ? mode : ((modes[0] && modes[0].key) || 'yr');
  const labels = briefing ? periodLabels(briefing.periods) : null;
  const customerName = briefing ? displayName(briefing.customer.name) : '';
  const lake = briefing && briefing.coverage && briefing.coverage.lake;

  return (
    <div className="cb">
      <div className="sheet">
        <div className="bar" />
        <div className="inner">
          <header className="mast">
            <div>
              <div className="wordmark">HighQ - Thomson Reuters</div>
              <h1>{customerName || 'Engineering briefing'}</h1>
              {briefing && (activeMode === 'yr' || activeMode === 'az')
                ? <p className="period">{periodLine(briefing.periods, activeMode)}</p>
                : null}
              <p className="audience">Engineering briefing{customerName ? ` for the meeting with ${customerName}` : ''}</p>
            </div>
            <div className="mast-side">
              <div className="conf">Confidential</div>
              <div className="modebar" role="tablist" aria-label="Report">
                {modes.map((item) => (
                  <button
                    type="button"
                    key={item.key}
                    role="tab"
                    aria-selected={activeMode === item.key ? 'true' : 'false'}
                    onClick={() => changeMode(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </header>

          {!briefing && !error ? <p className="note" role="status">Loading Data Lake cases&hellip;</p> : null}
          {error ? <p className="note" role="alert">Could not load the briefing: {error}</p> : null}

          {briefing ? (
            <>
              <HowTo briefing={briefing} mode={activeMode} />
              {activeMode === 'perf' ? null : (
                <p className="note">
                  Data Lake: {fmtInt(lake && lake.y25)} cases in {labels.first} &middot; {fmtInt(lake && lake.y26)} in {labels.second}
                </p>
              )}
              {activeMode === 'yr' || activeMode === 'az' ? <TabBar tab={tab} onChange={setTab} /> : null}
              <Panel name={name} mode={activeMode} tab={tab} briefing={briefing} study={study} onStudy={handleStudy} />
              {briefing.dataNotes && briefing.dataNotes.limitedData ? (
                <p className="note">
                  Few cases were raised in one of the periods, so percentages and rates here can change a lot with a
                  single case. Read them alongside the case counts.
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
